'use client'

import { stringToSlug } from '@/lib/utils'
import { Option } from '@/types/product'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { useEffect, useMemo, useRef } from 'react'
import { useProductDetailSafe } from '../../context/product-detail-context'

// Import components từ shadcn/ui
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { useIsMobileClient } from '@/hooks/common/use-device-type'
import { safeTranslateColor } from '@/lib/helpers/sanitize-i18n-keys'
import { useTranslations } from 'next-intl'
import { usePathname, useSearchParams } from 'next/navigation'
import { useProductDetailStore } from '../../hooks/use-product-detail-store'
import { ProductInfoVariantItem } from './product-info-variant-item'

export const ProductInfoVariants = () => {
  const isMobile = useIsMobileClient()
  const {
    colorSelected,
    colorOptionActive,
    sortedColorOptions,
    product,
    scrollToFirstImageRef,
  } = useProductDetailSafe()
  const t = useTranslations('title')
  const tColorTitle = useTranslations()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  // Get centralized logic from the store
  const {
    handleChangeColor,
    isOpen,
    setIsOpen,
    colorsPerRow,
    setColorsPerRow,
  } = useProductDetailStore()

  // Tham chiếu để tính số màu trên mỗi dòng
  const containerRef = useRef<HTMLUListElement>(null)
  const itemRef = useRef<HTMLAnchorElement>(null)

  // Build URL với query parameter color cho mỗi variant
  const buildColorUrl = useMemo(() => {
    return (colorSlug: string) => {
      const params = new URLSearchParams(searchParams.toString())
      params.set('color', colorSlug)
      return `${pathname}?${params.toString()}`
    }
  }, [pathname, searchParams])

  // Tự động tính số màu trên mỗi dòng dựa vào width container và width thực tế của item
  useEffect(() => {
    const updateColorsPerRow = () => {
      if (!containerRef.current || !itemRef.current) return
      const containerWidth = containerRef.current.offsetWidth
      const itemWidth = itemRef.current.offsetWidth
      if (itemWidth > 0) {
        setColorsPerRow(
          Math.max(
            1,
            Math.min(isMobile ? 5 : 8, Math.floor(containerWidth / itemWidth))
          )
        )
      }
    }

    updateColorsPerRow()
    window.addEventListener('resize', updateColorsPerRow)
    return () => window.removeEventListener('resize', updateColorsPerRow)
  }, [isMobile, setColorsPerRow])

  // Số màu tối đa khi collapse (2 dòng)
  const maxToShow = colorsPerRow * 2
  const showCollapse = sortedColorOptions.length > maxToShow

  const titleVariant = product?.options?.find(
    (option) => option.option_id === 'color_perfume'
  )

  if (!sortedColorOptions.length) return null

  return (
    <div>
      <p className='mb-1 font-sans text-sm leading-4.5 text-neutral-600 lg:mb-2 lg:text-base lg:leading-5'>
        <span className='text-neutral-900 max-lg:hidden'>
          {titleVariant ? t('type') : t('color')}:
        </span>{' '}
        {safeTranslateColor(tColorTitle, colorOptionActive?.title || '')}
      </p>

      <Collapsible open={isOpen} onOpenChange={setIsOpen} className='w-full'>
        {/* Phần luôn hiển thị (2 dòng đầu tiên) */}
        <ul
          ref={containerRef}
          className='grid gap-2.5 gap-y-1.5 md:flex md:flex-wrap lg:gap-3 lg:gap-y-3'
          style={{
            gridTemplateColumns: `repeat(${colorsPerRow}, minmax(0, 1fr))`,
          }}
        >
          {sortedColorOptions
            .slice(0, maxToShow)
            .map((variant: Option, idx: number) => {
              const colorSlug = stringToSlug(variant.title)
              const isActive = colorSelected === colorSlug
              const colorUrl = buildColorUrl(colorSlug)
              return (
                <li className='col-span-1' key={variant.title}>
                  <ProductInfoVariantItem
                    isActive={isActive}
                    onClick={() => {
                    handleChangeColor(variant)
                    scrollToFirstImageRef.current?.()
                  }}
                    href={colorUrl}
                    title={variant.title}
                    imageUrl={variant?.image?.[variant?.image?.length - 1]?.src}
                    linkRef={idx === 0 ? itemRef : undefined}
                  />
                </li>
              )
            })}
        </ul>

        {/* Phần có thể collapse/expand */}
        <CollapsibleContent className='overflow-hidden transition-all duration-1000 ease-in-out'>
          <ul
            className='mt-1 grid gap-2.5 gap-y-1.5 md:flex md:flex-wrap lg:gap-3 lg:gap-y-3'
            style={{
              gridTemplateColumns: `repeat(${colorsPerRow}, minmax(0, 1fr))`,
            }}
          >
            {sortedColorOptions.slice(maxToShow).map((variant: Option) => {
              const colorSlug = stringToSlug(variant.title)
              const isActive = colorSelected === colorSlug
              const colorUrl = buildColorUrl(colorSlug)
              return (
                <li className='col-span-1' key={variant.title}>
                  <ProductInfoVariantItem
                    isActive={isActive}
                    onClick={() => {
                    handleChangeColor(variant)
                    scrollToFirstImageRef.current?.()
                  }}
                    href={colorUrl}
                    title={variant.title}
                    imageUrl={variant?.image?.[variant?.image?.length - 1]?.src}
                  />
                </li>
              )
            })}
          </ul>
        </CollapsibleContent>

        {/* Nút mở rộng/thu gọn */}
        {showCollapse && (
          <CollapsibleTrigger asChild>
            <div className='mr-3 mt-2 flex cursor-pointer items-center justify-center gap-2 text-neutral-600'>
              {!isOpen ? (
                <ChevronDown className='h-6 w-6' />
              ) : (
                <ChevronUp className='h-6 w-6' />
              )}
            </div>
          </CollapsibleTrigger>
        )}
      </Collapsible>
    </div>
  )
}
